export default function WindermereDestinationsSection() {
  const stats = [
    {
      value: "62%",
      label: "of Windermere requests are family or cruise-related with under 4 weeks notice",
    },
    {
      value: "2–3 weeks",
      label: "average notice for convention-related business travel to Europe",
    },
    {
      value: "Mar–Apr",
      label: "peak spring break season — passport renewals spike before Disney cruises",
    },
    {
      value: "48-hour",
      label: "shortest convention departure window we've accommodated for a Windermere executive",
    },
  ];

  const destinations = [
    {
      icon: "ri-map-pin-2-line",
      name: "The Bahamas & Caribbean",
      context: "The most common leisure destination for Windermere families — whether by Disney Cruise Line, Royal Caribbean, or Norwegian from Port Canaveral, or by direct flight from MCO to Nassau, Jamaica, or the Dominican Republic. Windermere families combine Disney park visits with Caribbean cruises during spring break and summer vacation, making these itineraries especially popular in March, June, and July.",
      urgency: "Cruise embarkation deadlines are immovable — passport issues discovered days before sailing require emergency processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "United Kingdom & Ireland",
      context: "London, Edinburgh, and Dublin are staple destinations for Windermere business travelers attending financial, pharmaceutical, and technology conferences. The Orange County Convention Center's international connections mean that Windermere executives frequently extend their convention attendance with European business meetings. London is also a popular family destination during summer break for cultural immersion and heritage exploration.",
      urgency: "Convention travel is often confirmed 2–3 weeks before departure — insufficient time for standard passport processing",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "France, Germany & Switzerland",
      context: "Paris, Frankfurt, and Basel draw Windermere business travelers for pharmaceutical summits, financial conferences, and luxury goods industry meetings. The Lake Nona Medical City corridor sends physicians and researchers to medical conferences in Berlin, Munich, and Geneva. These destinations require six months of passport validity, which many travelers discover only when checking in at MCO.",
      urgency: "European business travel is often booked 2–4 weeks before departure — too short for standard processing during peak season",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Mexico & Latin America",
      context: "Cancun, Riviera Maya, and Los Cabos are staple spring break and winter escape destinations for Windermere families seeking beach vacations within a short flight from MCO. Business travelers also fly to Mexico City, São Paulo, and Bogotá for corporate meetings and hospitality industry partnerships. Mexico requires six months of passport validity for entry.",
      urgency: "Spring break bookings surge in January — passport discoveries spike in February and March for Windermere families",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Canada",
      context: "Toronto, Vancouver, and Montreal are frequent business destinations for Windermere executives in finance, technology, and hospitality. The relatively short flight from MCO to Toronto (approximately 2.5 hours) makes Canada an accessible international destination for same-day or overnight business trips. Canadian conferences also draw physicians and researchers from the Lake Nona corridor.",
      urgency: "Same-week Canadian business travel is common — a passport discovered expired the day before a Toronto meeting is a crisis",
    },
    {
      icon: "ri-map-pin-2-line",
      name: "Spain, Italy & Portugal",
      context: "Barcelona, Rome, and Lisbon draw Windermere families for summer cultural vacations and Windermere Preparatory School students for semester-abroad and exchange programs. These Mediterranean destinations are popular among affluent families seeking European cultural immersion after years of theme park vacations. Portugal's growing popularity as a retirement and investment destination also draws Windermere-area retirees and real estate investors.",
      urgency: "Study-abroad program confirmations often arrive 3–4 weeks before departure — too late for standard processing",
    },
  ];

  return (
    <section id="destinations" className="bg-white py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Where Windermere Travelers Go</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Popular International Destinations from Windermere
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Windermere travelers head to the Caribbean for family cruises, Europe for business and cultural travel, and Latin America for leisure and corporate partnerships. These are the destinations that create the most passport urgency for our Windermere clients.
          </p>
        </div>

        {/* Stat blocks */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-14">
          {stats.map((stat, i) => (
            <div key={i} className="bg-[#F7F8FA] rounded-lg p-5 border border-gray-200 text-center">
              <p className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-1">{stat.value}</p>
              <p className="text-gray-500 text-xs leading-relaxed">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Destination cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {destinations.map((dest, i) => (
            <div
              key={i}
              className="bg-[#F7F8FA] rounded-lg border border-gray-200 p-6 hover:border-[#F6C344] transition-all duration-200 flex flex-col"
            >
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-white border border-gray-200 flex-shrink-0">
                  <i className={`${dest.icon} text-[#1A365D] text-sm`}></i>
                </div>
                <h3 className="font-bold text-[#1A365D] text-sm">{dest.name}</h3>
              </div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 flex-1">{dest.context}</p>
              <div className="bg-white rounded-md p-3 border border-gray-200">
                <p className="text-[#F6C344] text-xs font-semibold mb-0.5"><i className="ri-time-line mr-1"></i>Urgency Pattern</p>
                <p className="text-gray-500 text-xs leading-relaxed">{dest.urgency}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Internal links */}
        <div className="text-center">
          <p className="text-gray-400 text-xs mb-3">Serving travelers throughout Central Florida</p>
          <div className="flex flex-wrap items-center justify-center gap-2">
            {[
              { name: "Orlando", href: "/orlando" },
              { name: "Winter Park", href: "/winter-park" },
              { name: "Kissimmee", href: "/kissimmee" },
              { name: "Tampa", href: "/tampa" },
              { name: "Lakeland", href: "/lakeland" },
              { name: "Sarasota", href: "/sarasota" },
            ].map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="inline-flex items-center gap-1 text-xs text-[#1A365D] hover:text-[#F6C344] font-medium transition-colors bg-[#F7F8FA] border border-gray-200 hover:border-[#F6C344] rounded-full px-3 py-1.5"
              >
                {link.name} <i className="ri-arrow-right-line text-[10px]"></i>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}